import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Lock, Mail, Loader2 } from "lucide-react";

export const AdminLogin = () => {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/admin" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin");
    } catch (e2) {
      setErr(
        e2?.response?.data?.detail || "No se pudo iniciar sesión. Verifica tus datos."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="admin-login-page"
      className="min-h-screen flex items-center justify-center bg-[#F9F8F6] px-6"
    >
      <div className="w-full max-w-md bg-white border border-[#E5E5E5] rounded-3xl p-10 shadow-sm">
        <a
          href="/"
          className="font-display text-2xl tracking-tight flex items-center gap-2 mb-10 text-[#1A1A1A]"
        >
          <span className="inline-block w-2 h-2 rounded-full bg-[#D4AF37]" />
          Multident
        </a>

        <p className="overline mb-3">— Acceso administrador</p>
        <h1 className="font-display font-light text-3xl text-[#1A1A1A] tracking-tight mb-8">
          Bienvenido de vuelta.
        </h1>

        <form onSubmit={onSubmit} className="space-y-5">
          <Field icon={<Mail size={16} />} label="Email">
            <input
              data-testid="login-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="tu@correo.com"
              className="md-input"
            />
          </Field>
          <Field icon={<Lock size={16} />} label="Contraseña">
            <input
              data-testid="login-password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="md-input"
            />
          </Field>

          {err && (
            <p
              data-testid="login-error"
              className="text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded-lg"
            >
              {err}
            </p>
          )}

          <button
            data-testid="login-submit"
            type="submit"
            disabled={loading}
            className="btn-premium w-full inline-flex items-center justify-center gap-2 bg-[#1A1A1A] text-white px-7 py-4 rounded-full text-sm font-medium disabled:opacity-60"
          >
            {loading && <Loader2 size={16} className="animate-spin" />}
            {loading ? "Ingresando…" : "Entrar"}
          </button>
        </form>

        <a
          href="/"
          className="block text-center mt-8 text-xs text-[#666] hover:text-[#1A1A1A] tracking-widest uppercase"
        >
          ← Volver al sitio
        </a>
      </div>

      <style>{`
        .md-input {
          width: 100%;
          padding: 14px 16px 14px 42px;
          background: #F9F8F6;
          border: 1px solid #E5E5E5;
          border-radius: 14px;
          color: #1A1A1A;
          font-family: 'Manrope', sans-serif;
          font-size: 14px;
          outline: none;
          transition: border-color .25s ease;
        }
        .md-input:focus { border-color: #D4AF37; background: #fff; }
      `}</style>
    </div>
  );
};

const Field = ({ icon, label, children }) => (
  <label className="block">
    <span className="block text-xs uppercase tracking-[0.18em] text-[#666] mb-2">
      {label}
    </span>
    <div className="relative">
      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#D4AF37]">
        {icon}
      </span>
      {children}
    </div>
  </label>
);
